import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { updateMessageForLanguageAction } from '../context/message-composer-actions';

import { UiRichTextEditor } from '../../../ui/ui-rich-text-editor';
import {
  useMessageComposerDispatch,
  useMessageInputState,
} from '../context/message-composer-context';

type Props = {
  onInstanceReady: (e: CKEditorEventInfo) => void;
  config: object;
  readOnly?: boolean;
  injectCKEditorLineEndings?: boolean;
};

export const RichTextEditorInContext = React.forwardRef(
  ({ onInstanceReady, config, readOnly, injectCKEditorLineEndings }: Props, mutableRef) => {
    const ref = mutableRef as RefObject<{ editor: CKEditorInstance | null } | null>;
    const {
      // potentially stale, only used on initial render and force updates
      message: messageFromContext,
      languageCode,
      isTranslating,
      streamingHackText,
      forceUpdate,
    } = useMessageInputState();
    const dispatch = useMessageComposerDispatch();

    // maintain message state within component to keep state updates as atomic
    // as possible
    const [message, setMessage] = useState<string>(messageFromContext);
    // set if a force update is ongoing
    const forceUpdateBeingApplied = useRef<boolean>(false);

    /**
     * debounce the dispatch to context every 25ms for performance
     * particularly important for CPU-constrained clients
     */
    const updateMessageContext = useMemo(
      () =>
        debounce((newMessage: string) => {
          dispatch(
            updateMessageForLanguageAction(
              { languageCode, message: newMessage },
              { forceUpdate: false },
            ),
          );
        }, 25),
      [dispatch, languageCode],
    );

    // We set the input data to CKEditor to `''` when translating because we put
    // a react portal on top of the editor to avoid the jittery behavior when
    // constantly updating the data
    useEffect(() => {
      if (streamingHackText) {
        setMessage('');
      }
    }, [streamingHackText]);

    useEffect(() => {
      if (!forceUpdate) {
        return;
      }

      // cancel future debounced updates
      updateMessageContext.cancel();
      // force update the component's state
      setMessage(messageFromContext);
      // mark force update as ongoing (If ckeditor isn't mounted we don't need to for update it)
      forceUpdateBeingApplied.current = !!ref.current?.editor?.setData;

      // we have to force a delayed setData call here because CKEditor doesn't
      // reliably register changes to its input data
      if (forceUpdateBeingApplied.current) {
        setTimeout(() => {
          ref.current?.editor?.setData(messageFromContext, {
            callback: () => {
              forceUpdateBeingApplied.current = false;
            },
          });
        }, 100);
      }
    }, [forceUpdate, messageFromContext, ref, updateMessageContext]);

    const handleMessageChange = (e: CKEditorEventInfo) => {
      if (
        !isTranslating &&
        !streamingHackText &&
        // only handle change events if a force update is not being applied
        !forceUpdateBeingApplied.current
      ) {
        if (e.editor?.getData) {
          const newMessage = e.editor?.getData();
          // immediately set the state of this component
          setMessage(newMessage);
          // debounce updates to context for slow clients
          updateMessageContext(newMessage);
        }
      }
    };

    return (
      <UiRichTextEditor
        ref={ref}
        data={message}
        onChange={handleMessageChange}
        onInstanceReady={onInstanceReady}
        config={config}
        readOnly={readOnly}
        injectCKEditorLineEndings={injectCKEditorLineEndings}
        languageCode={languageCode}
      />
    );
  },
);
